import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { Layout } from '@/components/layout';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { logoutUser } from '@/features/auth/authThunks';
import { BootstrapPhase } from '@/features/app-state';
import { selectActiveInvitationToken } from '@/features/invitations';

export interface ProtectedRouteProps {
  requiredRoles?: string[];
  requiredPermission?: string;
  requiredAnyPermission?: string[];
  superAdminOnly?: boolean;
  allowDuringOnboarding?: boolean;

  /**
   * If true, do NOT render the Layout wrapper (useful for full-screen flows like onboarding).
   * Default: false
   */
  noLayout?: boolean;
}

const ONBOARDING_PREFIX = '/onboarding';

export function ProtectedRoute({
  requiredRoles,
  requiredPermission,
  requiredAnyPermission,
  superAdminOnly,
  allowDuringOnboarding = false,
  noLayout = false,
}: ProtectedRouteProps = {}) {
  const dispatch = useAppDispatch();

  const bootstrapPhase = useAppSelector((state) => state.appState.bootstrapPhase);
  const bootstrapMessage = useAppSelector((state) => state.appState.bootstrapMessage);
  const bootstrapError = useAppSelector((state) => state.appState.bootstrapError);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const usersNeedOnboarding = useAppSelector((state) => state.users.needsOnboarding);
  const activeInvitationToken = useAppSelector(selectActiveInvitationToken);

  const location = useLocation();
  const { ready, hasPermission, hasAnyPermission, hasRole, isSuperAdmin } = usePermissions();

  // Auto-logout when access token is invalid/expired
  useEffect(() => {
    if (bootstrapPhase === BootstrapPhase.Error && bootstrapError?.includes('No valid access token available')) {
      dispatch(logoutUser());
    }
  }, [bootstrapPhase, bootstrapError, dispatch]);

  const showBootstrapSpinner =
    bootstrapPhase === BootstrapPhase.Idle ||
    bootstrapPhase === BootstrapPhase.RestoringSession ||
    bootstrapPhase === BootstrapPhase.LoadingProfile ||
    bootstrapPhase === BootstrapPhase.LoadingMemberships ||
    bootstrapPhase === BootstrapPhase.EvaluatingOnboarding ||
    bootstrapPhase === BootstrapPhase.LoadingRole ||
    bootstrapPhase === BootstrapPhase.LoadingOrganization;

  if (showBootstrapSpinner) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <LoadingSpinner size={160} />
        <p className="text-muted-foreground text-sm">{bootstrapMessage ?? 'Bootstrapping app…'}</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (bootstrapPhase === BootstrapPhase.Unauthenticated || !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  const isOnboardingRoute = location.pathname.startsWith(ONBOARDING_PREFIX);
  const shouldGateForOnboarding = !isSuperAdmin || location.pathname === '/';

  // Super admins may use admin tooling while onboarding, but their dashboard remains gated.
  if (
    bootstrapPhase === BootstrapPhase.OnboardingRequired &&
    usersNeedOnboarding &&
    shouldGateForOnboarding &&
    !allowDuringOnboarding &&
    !isOnboardingRoute
  ) {
    if (activeInvitationToken) {
      return <Navigate to="/invite/accept" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  if (bootstrapPhase === BootstrapPhase.Error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
        <p className="text-destructive">Failed to bootstrap app: {bootstrapError}</p>
        <div className="flex items-center gap-4">
          <button onClick={() => window.location.reload()} className="text-primary hover:underline">
            Retry
          </button>
          <button onClick={() => void dispatch(logoutUser())} className="text-muted-foreground hover:underline">
            Log out
          </button>
        </div>
      </div>
    );
  }

  // Before checking strict permissions, wait for role to load when route depends on it.
  const requiresRoleGate = Boolean(
    superAdminOnly || requiredRoles?.length || requiredPermission || requiredAnyPermission?.length,
  );
  const waitingForRole = requiresRoleGate && !isSuperAdmin && !ready;

  if (waitingForRole) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <LoadingSpinner size={160} />
        <p className="text-muted-foreground text-sm">Loading your permissions…</p>
      </div>
    );
  }

  // Permission gates (only meaningful when profile exists)
  // On onboarding route, you might want to skip permission gating entirely.
  // Keep it enabled by default; if you prefer skipping, add: if (isOnboardingRoute) return <Outlet />
  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredRoles && !requiredRoles.some((r) => hasRole(r))) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredAnyPermission && !hasAnyPermission(requiredAnyPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render without Layout when requested (useful for onboarding pages if you route them separately)
  if (noLayout) {
    return <Outlet />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}
